/**
 * DATA LAYER (DL): FIGHTERS
 * Módulo de comunicación REST nativa (Fetch API) con el backend de Interaktia-V2 para el juego Fighters.
 * Gestiona el CRUD de temas, configuración, historial de combates, subida de recursos multimedia,
 * comandos de control autoritativo de sala y telemetría de aficionados.
 * 
 * @module utilities/DL/Fighters
 */

import SignalRService from '../WS/SignalRService';

/**
 * Genera o recupera un identificador único persistente para el dispositivo del aficionado.
 * @returns {string} ID único de cliente aficionado.
 */
export const getFanClientId = () => {
    if (typeof localStorage !== 'undefined') {
        let clientId = localStorage.getItem('fighters_fan_client_id');
        if (!clientId) {
            clientId = 'fan_' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
            localStorage.setItem('fighters_fan_client_id', clientId);
        }
        return clientId;
    }
    if (typeof sessionStorage !== 'undefined') {
        let clientId = sessionStorage.getItem('fighters_fan_client_id');
        if (!clientId) {
            clientId = 'fan_' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
            sessionStorage.setItem('fighters_fan_client_id', clientId);
        }
        return clientId;
    }
    return 'fan_' + Math.random().toString(36).substring(2, 15);
};

/**
 * Resuelve y retorna la URL base para los endpoints de Fighters.
 * @returns {string} URL base formateada para el controlador de Fighters.
 */
const getApiBaseUrl = () => {
    const rawApiUrl = process.env.REACT_APP_API_URL || 'https://localhost:50586/api/';
    return rawApiUrl.endsWith('/') ? `${rawApiUrl}fighters` : `${rawApiUrl}/fighters`;
};

// =========================================================================
// CRUD DE TEMAS VISUALES
// =========================================================================

/**
 * Obtiene la lista de temas visuales disponibles para Fighters con soporte multi-tenant.
 * @param {string|null} [roomId=null] - Identificador único de la sala (opcional).
 * @param {string|null} [operatorId=null] - Identificador único del operador (opcional).
 * @returns {Promise<{success: boolean, data?: Array<Object>, message?: string}>} Respuesta con la colección de temas.
 */
export const GetThemesDL = async (roomId = null, operatorId = null) => {
    try {
        const queryParameters = new URLSearchParams();
        if (roomId) {
            queryParameters.append('roomId', roomId);
        }
        if (operatorId) {
            queryParameters.append('operatorId', operatorId);
        }

        const queryString = queryParameters.toString();
        const requestUrl = `${getApiBaseUrl()}/themes${queryString ? `?${queryString}` : ''}`;

        const httpResponse = await fetch(requestUrl, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al consultar los temas de Fighters.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red o conexión al consultar los temas de Fighters.'
        };
    }
};

/**
 * Obtiene el detalle de un tema específico por su identificador numérico.
 * @param {number|string} themeIdentifier - Identificador único del tema.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Respuesta con el tema encontrado.
 */
export const GetThemeByIdDL = async (themeIdentifier) => {
    try {
        const sanitizedId = encodeURIComponent(String(themeIdentifier));
        const requestUrl = `${getApiBaseUrl()}/theme/${sanitizedId}`;
        const httpResponse = await fetch(requestUrl, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al obtener el tema ${themeIdentifier}.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData
        };
    } catch (networkError) {
        return {
            success: false,
            message: `Error de red al consultar el tema con ID ${themeIdentifier}.`
        };
    }
};

/**
 * Crea un nuevo tema personalizado de Fighters.
 * @param {Object} themePayload - Objeto con los datos del nuevo tema.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Tema creado y confirmado.
 */
export const CreateThemeDL = async (themePayload) => {
    try {
        const requestUrl = `${getApiBaseUrl()}/theme`;
        const httpResponse = await fetch(requestUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(themePayload)
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al crear el tema.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Tema creado exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al crear el tema de Fighters.'
        };
    }
};

/**
 * Actualiza la información y recursos de un tema existente.
 * @param {number|string} themeIdentifier - Identificador del tema a actualizar.
 * @param {Object} themePayload - Datos modificados del tema.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Resultado de la actualización.
 */
export const UpdateThemeDL = async (themeIdentifier, themePayload) => {
    try {
        const sanitizedId = encodeURIComponent(String(themeIdentifier));
        const requestUrl = `${getApiBaseUrl()}/theme/${sanitizedId}`;
        const httpResponse = await fetch(requestUrl, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(themePayload)
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al actualizar el tema.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Tema actualizado exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al actualizar el tema de Fighters.'
        };
    }
};

/**
 * Elimina un tema personalizado de Fighters.
 * @param {number|string} themeIdentifier - Identificador del tema a eliminar.
 * @returns {Promise<{success: boolean, message?: string}>} Confirmación de eliminación.
 */
export const DeleteThemeDL = async (themeIdentifier) => {
    try {
        const sanitizedId = encodeURIComponent(String(themeIdentifier));
        const requestUrl = `${getApiBaseUrl()}/theme/${sanitizedId}`;
        const httpResponse = await fetch(requestUrl, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json'
            }
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al eliminar el tema.`
            };
        }

        const responseData = await httpResponse.json().catch(() => ({}));
        return {
            success: true,
            message: responseData.message || 'Tema eliminado exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al eliminar el tema de Fighters.'
        };
    }
};

/**
 * Clona un tema existente generando una nueva copia editable.
 * @param {number|string} themeIdentifier - Identificador del tema original a clonar.
 * @param {string|null} [newThemeName=null] - Nombre opcional para la copia clonada.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Tema clonado.
 */
export const CloneThemeDL = async (themeIdentifier, newThemeName = null) => {
    try {
        const sanitizedId = encodeURIComponent(String(themeIdentifier));
        const requestUrl = `${getApiBaseUrl()}/theme/${sanitizedId}/clone`;
        const requestBody = newThemeName ? { newName: newThemeName } : {};

        const httpResponse = await fetch(requestUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(requestBody)
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al clonar el tema.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Tema clonado exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al clonar el tema de Fighters.'
        };
    }
};

// =========================================================================
// CONFIGURACIÓN E HISTORIAL DE PARTIDAS
// =========================================================================

/**
 * Obtiene la configuración activa de Fighters para una sala determinada.
 * @param {string} roomId - Identificador GUID de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Configuración activa.
 */
export const GetConfigDL = async (roomId) => {
    try {
        const sanitizedRoomId = encodeURIComponent(String(roomId));
        const requestUrl = `${getApiBaseUrl()}/config/${sanitizedRoomId}`;
        const httpResponse = await fetch(requestUrl, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al obtener la configuración.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al obtener la configuración de Fighters.'
        };
    }
};

/**
 * Guarda o actualiza la configuración de Fighters para una sala.
 * @param {Object} configPayload - Datos de configuración a persistir.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Configuración persistida.
 */
export const SaveConfigDL = async (configPayload) => {
    try {
        const requestUrl = `${getApiBaseUrl()}/config`;
        const httpResponse = await fetch(requestUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(configPayload)
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al guardar la configuración.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Configuración guardada exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al guardar la configuración de Fighters.'
        };
    }
};

/**
 * Obtiene el historial de partidas disputadas en una sala.
 * @param {string} roomId - Identificador GUID de la sala.
 * @param {number} [limitQuantity=20] - Cantidad máxima de registros históricos a recuperar.
 * @returns {Promise<{success: boolean, data?: Array<Object>, message?: string}>} Colección de partidas históricas.
 */
export const GetMatchHistoryDL = async (roomId, limitQuantity = 20) => {
    try {
        const sanitizedRoomId = encodeURIComponent(String(roomId));
        const sanitizedLimit = encodeURIComponent(String(limitQuantity));
        const requestUrl = `${getApiBaseUrl()}/history/${sanitizedRoomId}?limit=${sanitizedLimit}`;
        const httpResponse = await fetch(requestUrl, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al obtener el historial de partidas.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al consultar el historial de partidas de Fighters.'
        };
    }
};

/**
 * Registra y persiste las métricas y resultado final de una partida de Fighters.
 * @param {Object} matchResultPayload - Métricas finales de la partida disputada.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Resultado persistido.
 */
export const SaveMatchResultDL = async (matchResultPayload) => {
    try {
        const requestUrl = `${getApiBaseUrl()}/history`;
        const httpResponse = await fetch(requestUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(matchResultPayload)
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al guardar el resultado de la partida.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Resultado de la partida guardado exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al guardar el resultado de la partida.'
        };
    }
};

// =========================================================================
// CARGA DE RECURSOS MULTIMEDIA
// =========================================================================

/**
 * Sube y optimiza un recurso gráfico individual para Fighters en formato multipart/form-data.
 * @param {File|Blob|FormData} fileOrFormData - Archivo o instancia de FormData a transmitir.
 * @returns {Promise<{success: boolean, data?: Object, url?: string, message?: string}>} URL y metadata del archivo.
 */
export const UploadAssetDL = async (fileOrFormData) => {
    try {
        let requestFormData = fileOrFormData;
        if (!(fileOrFormData instanceof FormData)) {
            requestFormData = new FormData();
            requestFormData.append('file', fileOrFormData);
        }

        const requestUrl = `${getApiBaseUrl()}/upload`;
        const httpResponse = await fetch(requestUrl, {
            method: 'POST',
            body: requestFormData
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al subir el archivo multimedia.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: Boolean(responseData.success !== false),
            data: responseData,
            url: responseData.url || (responseData.data && responseData.data.url),
            message: responseData.message || 'Archivo subido y procesado exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al subir el archivo multimedia a Fighters.'
        };
    }
};

/**
 * Sube y optimiza un lote de hasta 50 archivos gráficos para la baraja o recursos de Fighters.
 * @param {Array<File>|FileList|FormData} filesOrFormData - Colección de archivos o FormData para envío masivo.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Reporte de procesamiento por lote.
 */
export const UploadBatchMediaDL = async (filesOrFormData) => {
    try {
        let requestFormData = filesOrFormData;
        if (!(filesOrFormData instanceof FormData)) {
            requestFormData = new FormData();
            const fileListArray = Array.from(filesOrFormData || []);
            fileListArray.forEach((fileItem) => {
                requestFormData.append('files', fileItem);
            });
        }

        const requestUrl = `${getApiBaseUrl()}/upload-batch`;
        const httpResponse = await fetch(requestUrl, {
            method: 'POST',
            body: requestFormData
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al procesar el lote de archivos.`
            };
        }

        const responseData = await httpResponse.json();
        const filesList = responseData.files || responseData.data?.files || [];
        const extractedUrls = (Array.isArray(responseData.urls) && responseData.urls.length > 0)
            ? responseData.urls
            : (Array.isArray(filesList) ? filesList.filter((f) => f.success && f.url).map((f) => f.url) : []);

        return {
            success: Boolean(responseData.success !== false && (extractedUrls.length > 0 || responseData.totalFiles === 0)),
            data: responseData,
            urls: extractedUrls,
            files: filesList,
            count: extractedUrls.length,
            message: responseData.message || 'Lote de archivos procesado exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al subir el lote multimedia de Fighters.'
        };
    }
};

// =========================================================================
// COMANDOS DE CONTROL DE SALA EN TIEMPO REAL
// =========================================================================

/**
 * Proyecta la portada de Fighters en la pantalla gigante y sincroniza a los aficionados.
 * @param {string} roomId - Identificador GUID de la sala.
 * @param {number|string} [themeIdentifier=0] - Identificador del tema visual a proyectar.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado actualizado de la partida.
 */
export const ShowCoverDL = async (roomId, themeIdentifier = 0) => {
    try {
        const requestUrl = `${getApiBaseUrl()}/control/cover`;
        const httpResponse = await fetch(requestUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                roomId: roomId,
                themeId: Number(themeIdentifier) || 0
            })
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al proyectar la portada.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Portada de Fighters proyectada exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al proyectar la portada de Fighters.'
        };
    }
};

/**
 * Proyecta la escena de combate (ring) con los avatares y barras de vida preparadas.
 * @param {string} roomId - Identificador GUID de la sala.
 * @param {number|string} [themeIdentifier=0] - Identificador del tema visual activo.
 * @param {string} [gameMode='Score'] - Modo de combate ('Score' | 'HealthKO' | 'HealthTimed' | 'Simultaneous').
 * @param {number} [durationSeconds=10] - Duración configurada del asalto en segundos.
 * @param {boolean} [showComboPopups=true] - Habilita popups de combos flotantes de aura.
 * @param {boolean} [showDeckTransforms=true] - Habilita transformaciones de baraja de sprites.
 * @param {number} [deckTransformInterval=15] - Intervalo de taps promedio para cambio de imagen.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado en vivo de la escena.
 */
export const ShowGameSceneDL = async (
    roomId,
    themeIdentifier = 0,
    gameMode = 'Score',
    durationSeconds = 10,
    showComboPopups = true,
    showDeckTransforms = true,
    deckTransformInterval = 15
) => {
    try {
        const requestUrl = `${getApiBaseUrl()}/control/game-scene`;
        const httpResponse = await fetch(requestUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                roomId: roomId,
                themeId: Number(themeIdentifier) || 0,
                gameMode: gameMode,
                durationSeconds: Number(durationSeconds) || 10,
                showComboPopups: Boolean(showComboPopups !== false),
                showDeckTransforms: Boolean(showDeckTransforms !== false),
                deckTransformInterval: Number(deckTransformInterval) || 15
            })
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al preparar la escena de juego.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Escena de combate preparada exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al preparar la escena de juego de Fighters.'
        };
    }
};

/**
 * Inicia la cuenta regresiva sincronizada (3, 2, 1) y posterior arranque del combate con telemetría a 100ms.
 * @param {string} roomId - Identificador GUID de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado del combate iniciado.
 */
export const StartCountdownDL = async (roomId) => {
    try {
        const requestUrl = `${getApiBaseUrl()}/control/start`;
        const httpResponse = await fetch(requestUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ roomId: roomId })
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al iniciar la cuenta regresiva.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Cuenta regresiva y combate iniciados.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al iniciar el combate de Fighters.'
        };
    }
};

/**
 * Pausa temporalmente el combate activo manteniendo el tiempo y puntajes acumulados.
 * @param {string} roomId - Identificador GUID de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Confirmación de pausa.
 */
export const PauseMatchDL = async (roomId) => {
    try {
        const requestUrl = `${getApiBaseUrl()}/control/pause`;
        const httpResponse = await fetch(requestUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ roomId: roomId })
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al pausar el combate.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Combate pausado exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al pausar el combate de Fighters.'
        };
    }
};

/**
 * Reanuda un combate pausado continuando el cronómetro autoritativo.
 * @param {string} roomId - Identificador GUID de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Confirmación de reanudación.
 */
export const ResumeMatchDL = async (roomId) => {
    try {
        const requestUrl = `${getApiBaseUrl()}/control/resume`;
        const httpResponse = await fetch(requestUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ roomId: roomId })
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al reanudar el combate.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Combate reanudado exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al reanudar el combate de Fighters.'
        };
    }
};

/**
 * Finaliza anticipadamente el combate activo, calcula el ganador y persiste en base de datos.
 * @param {string} roomId - Identificador GUID de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Resultado final calculado.
 */
export const FinishEarlyDL = async (roomId) => {
    try {
        const requestUrl = `${getApiBaseUrl()}/control/finish-early`;
        const httpResponse = await fetch(requestUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ roomId: roomId })
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al finalizar el combate.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Combate finalizado anticipadamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al finalizar anticipadamente el combate de Fighters.'
        };
    }
};

/**
 * Reinicia la partida de la sala devolviéndola al estado inactivo con puntuaciones en cero.
 * @param {string} roomId - Identificador GUID de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado reiniciado.
 */
export const ResetMatchDL = async (roomId) => {
    try {
        const requestUrl = `${getApiBaseUrl()}/control/reset`;
        const httpResponse = await fetch(requestUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ roomId: roomId })
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al reiniciar el combate.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Combate reiniciado exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al reiniciar el combate de Fighters.'
        };
    }
};

/**
 * Consulta el estado actual en memoria RAM de la partida para una sala específica.
 * @param {string} roomId - Identificador GUID de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado autoritativo en vivo.
 */
export const GetMatchStateDL = async (roomId) => {
    try {
        const sanitizedRoomId = encodeURIComponent(String(roomId));
        const requestUrl = `${getApiBaseUrl()}/state/${sanitizedRoomId}`;
        const httpResponse = await fetch(requestUrl, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al consultar el estado de la partida.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al consultar el estado de la partida de Fighters.'
        };
    }
};

// =========================================================================
// TELEMETRÍA Y AFICIONADOS (FANS)
// =========================================================================

/**
 * Envía un lote acumulado de toques (taps) emitidos por un aficionado móvil.
 * @param {string} roomId - Identificador GUID de la sala.
 * @param {string} playerChoice - Bando elegido por el aficionado ('player1' | 'player2').
 * @param {number} [tapCount=1] - Cantidad de toques acumulados en la ventana de envío.
 * @param {string} [connectionId=''] - Identificador SignalR de la conexión del aficionado.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado actualizado tras procesar los taps.
 */
export const SendTapBatchDL = async (roomId, playerChoice, tapCount = 1, connectionId = '') => {
    try {
        const activeConnectionId = connectionId || SignalRService.getConnectionId() || getFanClientId();
        const requestUrl = `${getApiBaseUrl()}/tap-batch`;
        const httpResponse = await fetch(requestUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                roomId: roomId,
                playerChoice: playerChoice,
                tapCount: Number(tapCount) || 1,
                connectionId: activeConnectionId
            })
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al transmitir el lote de taps.`
            };
        }

        const responseData = await httpResponse.json().catch(() => ({ success: true }));
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al enviar el lote de taps.'
        };
    }
};

/**
 * Registra formalmente la elección de bando de un aficionado para el cálculo del promedio ponderado.
 * @param {string} roomId - Identificador GUID de la sala.
 * @param {string} playerChoice - Bando seleccionado ('player1' | 'player2').
 * @param {string} [connectionId=''] - Identificador SignalR de la conexión activa.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Confirmación de registro de aficionado.
 */
export const RegisterFanDL = async (roomId, playerChoice, connectionId = '') => {
    try {
        const activeConnectionId = connectionId || SignalRService.getConnectionId() || getFanClientId();
        const requestUrl = `${getApiBaseUrl()}/fan/register`;
        const httpResponse = await fetch(requestUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                roomId: roomId,
                playerChoice: playerChoice,
                connectionId: activeConnectionId
            })
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al registrar aficionado.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Aficionado registrado al luchador exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al registrar aficionado en Fighters.'
        };
    }
};

/**
 * Desregistra un aficionado cuando se desconecta o abandona la vista interactiva.
 * @param {string} roomId - Identificador GUID de la sala.
 * @param {string} [connectionId=''] - Identificador SignalR de la conexión del aficionado.
 * @returns {Promise<{success: boolean, message?: string}>} Confirmación de desregistro.
 */
export const UnregisterFanDL = async (roomId, connectionId = '') => {
    try {
        const activeConnectionId = connectionId || SignalRService.getConnectionId() || getFanClientId();
        const requestUrl = `${getApiBaseUrl()}/fan/unregister`;
        const httpResponse = await fetch(requestUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                roomId: roomId,
                connectionId: activeConnectionId,
                playerChoice: 'player1'
            })
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al desregistrar aficionado.`
            };
        }

        const responseData = await httpResponse.json().catch(() => ({}));
        return {
            success: true,
            message: responseData.message || 'Aficionado desregistrado exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al desregistrar aficionado en Fighters.'
        };
    }
};

export default {
    GetThemesDL,
    GetThemeByIdDL,
    CreateThemeDL,
    UpdateThemeDL,
    DeleteThemeDL,
    CloneThemeDL,
    GetConfigDL,
    SaveConfigDL,
    GetMatchHistoryDL,
    SaveMatchResultDL,
    UploadAssetDL,
    UploadBatchMediaDL,
    ShowCoverDL,
    ShowGameSceneDL,
    StartCountdownDL,
    PauseMatchDL,
    ResumeMatchDL,
    FinishEarlyDL,
    ResetMatchDL,
    GetMatchStateDL,
    SendTapBatchDL,
    RegisterFanDL,
    UnregisterFanDL
};
